import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Navigation from '../components/Navigation';
import UpgradeModal from '../components/UpgradeModal';
import QuotaDisplay from '../components/QuotaDisplay';
import QuotaUpgradeModal from '../components/QuotaUpgradeModal';
import { useFeatureAccess } from '../hooks/useFeatureAccess';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Users, TrendingUp, FileCheck, Shield, Edit, Eye, Lock, ChevronRight, GitBranch, Calendar } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function InvestorDashboard() {
  const { isDark } = useTheme();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [matches, setMatches] = useState([]);
  const [recommendations, setRecommendations] = useState([]);
  const [milestoneStats, setMilestoneStats] = useState({ total: 0, by_state: {}, scheduled_sessions: 0, next_session: null });
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeFeature, setUpgradeFeature] = useState('');
  const { upgradeModal, checkFeature, closeUpgradeModal } = useFeatureAccess();

  // Theme classes
  const bg = isDark ? 'bg-slate-900' : 'bg-slate-100';
  const cardBg = isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200';
  const innerCardBg = isDark ? 'bg-slate-700 border-slate-600' : 'bg-slate-50 border-slate-200';
  const textPrimary = isDark ? 'text-white' : 'text-blue-900';
  const textSecondary = isDark ? 'text-slate-300' : 'text-blue-700';
  const textMuted = isDark ? 'text-slate-400' : 'text-slate-600';
  const badgeBg = isDark ? 'bg-blue-900/60 text-blue-200 border border-blue-700/50' : 'bg-blue-100 text-blue-800';
  const badgeBgAlt = isDark ? 'bg-slate-600 text-slate-100 border border-slate-500' : 'bg-slate-200 text-slate-700';
  const btnSecondary = isDark ? 'border-slate-600 text-slate-200 hover:bg-slate-700' : 'border-slate-300 text-slate-700 hover:bg-slate-100';
  const verifiedColor = isDark ? 'text-green-400' : 'text-green-600';
  const hoverCard = isDark ? 'hover:border-blue-400' : 'hover:border-blue-500';

  const fetchData = async () => {
    try {
      const [profileRes, matchesRes, recsRes, milestoneStatsRes] = await Promise.all([
        axios.get(`${API}/profile/my-profile`),
        axios.get(`${API}/matches`),
        axios.get(`${API}/discovery/recommendations`),
        axios.get(`${API}/milestones/summary/stats`)
      ]);

      if (profileRes.data.exists) {
        setProfile(profileRes.data.profile);
      }
      setMatches(matchesRes.data);
      setRecommendations(recsRes.data.slice(0, 5));
      setMilestoneStats(milestoneStatsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const isPro = user?.plan_status !== 'free';

  // Card click handlers with tier gating
  const handleCardClick = (cardType) => {
    switch (cardType) {
      case 'matches':
        navigate('/matches');
        break;
      case 'ndas':
        if (isPro) {
          // Navigate to NDA management (future feature)
          navigate('/matches');
        } else {
          setUpgradeFeature('NDA Management');
          setShowUpgradeModal(true);
        }
        break;
      case 'relationships':
        navigate('/relationships');
        break;
      default:
        break;
    }
  };

  return (
    <div className={`min-h-screen ${bg}`} data-testid="investor-dashboard">
      <Navigation />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <div className="mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className={`text-3xl sm:text-4xl font-bold mb-2 ${textPrimary}`}>Investor Dashboard</h1>
            <p className={textSecondary}>Discover and invest in promising startups.</p>
          </div>
          {profile && (
            <div className="flex gap-3">
              <Link to="/profile-setup">
                <Button variant="outline" className={`border ${isDark ? 'border-slate-600 text-slate-200 hover:bg-slate-700' : 'border-slate-300 text-slate-700 hover:bg-slate-100'}`} data-testid="edit-profile-btn">
                  <Edit size={16} className="mr-2" />
                  Edit Profile
                </Button>
              </Link>
              <Link to={`/profile/${user?.user_id}?preview=founder`}>
                <Button className="btn-primary" data-testid="preview-profile-btn">
                  <Eye size={16} className="mr-2" />
                  Preview as Founder
                </Button>
              </Link>
            </div>
          )}
        </div>

        {!profile ? (
          <Card className={`shadow-sm p-8 mb-8 ${cardBg}`}>
            <div className="text-center">
              <h2 className={`text-2xl font-bold mb-4 ${textPrimary}`}>Complete Your Profile</h2>
              <p className={`mb-6 ${textSecondary}`}>Set up your investor profile to start discovering startups</p>
              <Link to="/profile-setup">
                <Button className="btn-primary" data-testid="setup-profile-btn">Set Up Profile</Button>
              </Link>
            </div>
          </Card>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              {/* Matches Card - clickable */}
              <Card 
                className={`shadow-sm p-6 cursor-pointer transition-all ${cardBg} ${hoverCard} hover:shadow-md`}
                onClick={() => handleCardClick('matches')}
                data-testid="card-matches"
              >
                <div className="flex items-center justify-between mb-2">
                  <span className={`text-sm font-medium ${textSecondary}`}>Matches</span>
                  <Users className="text-blue-500" size={20} />
                </div>
                <div className={`text-3xl font-bold ${textPrimary}`}>{matches.length}</div>
                <div className={`flex items-center mt-2 text-xs ${textMuted}`}>
                  <span>View all matches</span>
                  <ChevronRight size={14} className="ml-1" />
                </div>
              </Card>

              {/* NDAs Signed Card - tier gated */}
              <Card 
                className={`shadow-sm p-6 cursor-pointer transition-all ${cardBg} ${isPro ? `${hoverCard} hover:shadow-md` : 'opacity-80'}`}
                onClick={() => handleCardClick('ndas')}
                data-testid="card-ndas"
              >
                <div className="flex items-center justify-between mb-2">
                  <span className={`text-sm font-medium ${textSecondary}`}>NDAs Signed</span>
                  <div className="flex items-center gap-1">
                    {!isPro && <Lock className="text-slate-400" size={14} />}
                    <FileCheck className="text-blue-500" size={20} />
                  </div>
                </div>
                <div className={`text-3xl font-bold ${textPrimary}`}>0</div>
                <div className={`flex items-center mt-2 text-xs ${textMuted}`}>
                  {isPro ? (
                    <>
                      <span>Manage NDAs</span>
                      <ChevronRight size={14} className="ml-1" />
                    </>
                  ) : (
                    <span className="text-amber-500">Pro feature</span>
                  )}
                </div>
              </Card>

              {/* Relationship Progress Card */}
              <Card 
                className={`shadow-sm p-6 cursor-pointer transition-all ${cardBg} ${hoverCard} hover:shadow-md`}
                onClick={() => handleCardClick('relationships')}
                data-testid="card-relationships"
              >
                <div className="flex items-center justify-between mb-2">
                  <span className={`text-sm font-medium ${textSecondary}`}>Relationship Progress</span>
                  <GitBranch className="text-blue-500" size={20} />
                </div>
                <div className={`text-3xl font-bold ${textPrimary}`}>{milestoneStats.total}</div>
                <div className="flex items-center gap-3 mt-2">
                  {milestoneStats.scheduled_sessions > 0 && (
                    <span className={`text-xs flex items-center gap-1 ${textMuted}`}>
                      <Calendar size={12} />
                      {milestoneStats.scheduled_sessions} scheduled
                    </span>
                  )}
                  {milestoneStats.next_session && (
                    <span className="text-xs text-green-500">
                      Next: {new Date(milestoneStats.next_session.slot).toLocaleDateString()}
                    </span>
                  )}
                </div>
                <div className={`flex items-center mt-1 text-xs ${textMuted}`}>
                  <span>Track milestones</span>
                  <ChevronRight size={14} className="ml-1" />
                </div>
              </Card>

              <Card className={`shadow-sm p-6 ${cardBg}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className={`text-sm font-medium ${textSecondary}`}>Verification</span>
                  <Shield className="text-blue-500" size={20} />
                </div>
                <div className="text-sm font-bold">
                  {profile.verified ? (
                    <span className={verifiedColor}>Verified</span>
                  ) : (
                    <span className={textMuted}>Pending</span>
                  )}
                </div>
              </Card>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
              {/* Quota Display Card */}
              <QuotaDisplay mode="full" showUpgrade={true} />
              
              <Card className={`shadow-sm p-6 ${cardBg}`}>
                <h2 className={`text-xl font-bold mb-4 ${textPrimary}`}>Investment Criteria</h2>
                <div className="space-y-3">
                  <div>
                    <span className={`text-sm font-medium ${textSecondary}`}>Investor Type</span>
                    <p className={`font-medium ${textPrimary}`}>{profile.investor_type}</p>
                  </div>
                  <div>
                    <span className={`text-sm font-medium ${textSecondary}`}>Ticket Size</span>
                    <p className={textPrimary}>
                      {profile.currency} {profile.ticket_size_min.toLocaleString()} - {profile.ticket_size_max.toLocaleString()}
                    </p>
                  </div>
                  <div>
                    <span className={`text-sm font-medium ${textSecondary}`}>Preferred Stages</span>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {profile.preferred_stages.map(stage => (
                        <span key={stage} className={`text-xs px-2 py-1 rounded font-medium ${badgeBg}`}>
                          {stage}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div>
                    <span className={`text-sm font-medium ${textSecondary}`}>Industries</span>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {profile.preferred_industries.map(ind => (
                        <span key={ind} className={`text-xs px-2 py-1 rounded font-medium ${badgeBg}`}>
                          {ind}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </Card>

              <Card className={`shadow-sm p-6 ${cardBg}`}>
                <h2 className={`text-xl font-bold mb-4 ${textPrimary}`}>Geography Focus</h2>
                <div className="flex flex-wrap gap-2">
                  {profile.geography_focus.map(geo => (
                    <span key={geo} className={`text-sm px-3 py-2 rounded border ${badgeBgAlt}`}>
                      {geo}
                    </span>
                  ))}
                </div>
              </Card>
            </div>

            <Card className={`shadow-sm p-6 ${cardBg}`}>
              <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-4">
                <h2 className={`text-xl font-bold ${textPrimary}`}>Top Recommended Startups</h2>
                <Link to="/discovery">
                  <Button className={`border ${btnSecondary}`} variant="outline" data-testid="view-all-startups">View All</Button>
                </Link>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {recommendations.slice(0, 6).map(rec => (
                  <Link key={rec.user_id} to={`/profile/${rec.user_id}`}>
                    <Card className={`p-4 cursor-pointer hover:shadow-md transition-all border ${innerCardBg} ${isDark ? 'hover:border-blue-400' : 'hover:border-blue-500'}`}>
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h3 className={`font-semibold ${textPrimary}`}>{rec.profile.startup_name}</h3>
                          <p className={`text-sm ${textMuted}`}>{rec.name}</p>
                        </div>
                        <span className="match-score-badge">{rec.match_score}%</span>
                      </div>
                      <p className={`text-sm mb-3 line-clamp-2 ${textSecondary}`}>{rec.profile.one_line_pitch}</p>
                      <div className="flex items-center justify-between">
                        <span className={`text-xs px-2 py-1 rounded font-medium ${badgeBg}`}>
                          {rec.profile.stage}
                        </span>
                        <span className={`text-xs ${textMuted}`}>
                          {rec.profile.city}
                        </span>
                      </div>
                    </Card>
                  </Link>
                ))}
              </div>
            </Card>
          </>
        )}
      </div>

      <UpgradeModal
        isOpen={upgradeModal.isOpen}
        onClose={closeUpgradeModal}
        feature={upgradeModal.feature}
        currentPlan={upgradeModal.currentPlan}
        availablePlans={upgradeModal.availablePlans}
        role="investor"
      />

      {/* Upgrade Modal for tier-gated features */}
      <QuotaUpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        feature={upgradeFeature}
        userRole={user?.role}
        quotaType="pro_feature"
      />
    </div>
  );
}
